import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useDeleteAdminNotification, useGetAdminNotifications } from "../../api/hooks/useNotification";
import { showError, showSuccess } from "../../helpers/toast";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const RecievedNotifications = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: notificationsData, isLoading } = useGetAdminNotifications({
    page: 1,
    limit: 100,
    search: debouncedSearchTerm,
  });
  const { mutate: deleteNotification, isPending: isDeleting } = useDeleteAdminNotification({
    onSuccess: (response) => {
      showSuccess(response?.message || "Notification deleted successfully");
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete notification");
    },
  });

  const notifications = notificationsData?.data?.list || [];

  const formatDate = (dateString?: string) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "--";
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  const handleDeleteClick = (id: string) => {
    setNotificationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (notificationToDelete) {
      deleteNotification(notificationToDelete);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Recieved Notifications</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Recieved Notifications
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onCross={() => setSearchTerm("")}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
          </div>
        </div>
        <TableContainer className="table_container">
          <Box className="heading"></Box>
          <Table sx={{ minWidth: 650 }} aria-label="simple table" className="v3">
            <TableHead>
              <TableRow hover>
                <TableCell>User Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : notifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No notifications found.
                  </TableCell>
                </TableRow>
              ) : (
                notifications.map((row: any) => (
                  <TableRow hover key={row._id}>
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img src="/static/images/user_placeholder.png" alt="" />
                        </figure>
                        <p>
                          <b>{row.senderName || "Admin"}</b>
                          <span>{row.senderEmail || "-"}</span>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{row.title || "--"}</TableCell>
                    <TableCell className="max_c">
                      <p>{row.message || "--"}</p>
                    </TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Box>
                          <IconButton
                            onClick={() => handleDeleteClick(row._id)}
                            disabled={isDeleting}
                          >
                            <img src="/static/images/trash2_icon.svg" alt="" />
                          </IconButton>
                        </Box>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default RecievedNotifications;
